import React from "react";
import { NavLink, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Inbox, Mail, Layers, Sparkles, FolderKanban, Newspaper, CalendarDays, Image, Users as UsersIcon, FileText, Settings, Palette, UserCircle, LogOut, MoreVertical, Scale } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/BrandContext";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";

const CONTENT = ["super_admin", "content_admin", "viewer"];
const ENQUIRY = ["super_admin", "enquiry_admin", "viewer"];
const SUPER = ["super_admin"];
const ALL = ["super_admin", "content_admin", "enquiry_admin", "viewer"];

const GROUPS = [
  { section: "Administration", items: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ALL },
  ]},
  { section: "Content", items: [
    { to: "/admin/activities", label: "Areas of Focus", icon: Layers, roles: CONTENT },
    { to: "/admin/initiatives", label: "Initiatives", icon: Sparkles, roles: CONTENT },
    { to: "/admin/projects", label: "Projects", icon: FolderKanban, roles: CONTENT },
    { to: "/admin/news", label: "News", icon: Newspaper, roles: CONTENT },
    { to: "/admin/events", label: "Events", icon: CalendarDays, roles: CONTENT },
    { to: "/admin/gallery", label: "Gallery", icon: Image, roles: CONTENT },
    { to: "/admin/leadership", label: "Leadership", icon: UsersIcon, roles: CONTENT },
    { to: "/admin/content", label: "Website Content", icon: FileText, roles: CONTENT },
    { to: "/admin/legal", label: "Legal & Policies", icon: Scale, roles: CONTENT },
  ]},
  { section: "Enquiries", items: [
    { to: "/admin/interest", label: "Expressions of Interest", icon: Inbox, roles: ENQUIRY },
    { to: "/admin/contact", label: "Contact Enquiries", icon: Mail, roles: ENQUIRY },
  ]},
];

const UTILITIES = [
  { to: "/admin/branding", label: "Logo & Branding", icon: Palette, roles: SUPER },
  { to: "/admin/settings", label: "Settings", icon: Settings, roles: SUPER },
  { to: "/admin/users", label: "User Management", icon: UsersIcon, roles: SUPER },
  { to: "/admin/profile", label: "My Profile", icon: UserCircle, roles: ALL },
];

export default function AdminLayout({ children, title }) {
  const { user, loading, logout } = useAuth();
  const { symbol, trustName, tagline } = useBrand();
  const navigate = useNavigate();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-ivory text-ink-muted">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const role = user.role;
  const visibleGroups = GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => i.roles.includes(role)) }))
    .filter((g) => g.items.length);
  const visibleUtils = UTILITIES.filter((u) => u.roles.includes(role));

  const Sidebar = () => (
    <>
      <div className="flex items-center gap-3 border-b border-gold/20 px-5 py-4">
        <img src={symbol} alt="logo" className="h-10 w-10 shrink-0 object-contain" />
        <div className="leading-tight">
          <div className="font-serif text-sm font-semibold text-maroon">{trustName}</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3">
        {visibleGroups.map((g) => (
          <div key={g.section} className="mb-4">
            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink-muted/70">{g.section}</div>
            {g.items.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} data-testid={`admin-nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={({ isActive }) => `mb-1 flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-maroon text-ivory" : "text-ink-muted hover:bg-ivory"}`}>
                <l.icon size={17} strokeWidth={1.6} /> {l.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="m-3 flex items-center gap-2 rounded-sm border border-gold/30 px-3 py-2.5 text-sm text-maroon hover:bg-ivory">
        <LogOut size={16} /> Sign Out
      </button>
    </>
  );

  return (
    <div className="flex min-h-screen bg-ivory">
      <aside className="hidden w-64 flex-col border-r border-gold/20 bg-white lg:flex">
        <Sidebar />
      </aside>

      <div className="flex-1 overflow-x-hidden">
        <header className="flex items-center justify-between gap-3 border-b border-gold/20 bg-white px-5 py-4 lg:px-8">
          <h1 className="font-serif text-xl text-maroon sm:text-2xl">{title}</h1>
          <div className="flex items-center gap-2">
            <nav className="hidden items-center gap-1 md:flex">
              {visibleUtils.map((u) => (
                <NavLink key={u.to} to={u.to} title={u.label}
                  data-testid={`admin-util-${u.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className={({ isActive }) => `flex items-center gap-1.5 rounded-sm px-2.5 py-2 text-xs transition-colors ${isActive ? "bg-maroon text-ivory" : "text-ink-muted hover:bg-ivory hover:text-maroon"}`}>
                  <u.icon size={15} strokeWidth={1.6} />
                  <span className="hidden xl:inline">{u.label}</span>
                </NavLink>
              ))}
              <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-header-logout" title="Sign Out"
                className="flex items-center gap-1.5 rounded-sm border border-gold/30 px-2.5 py-2 text-xs text-maroon transition-colors hover:bg-ivory">
                <LogOut size={15} strokeWidth={1.6} /><span className="hidden xl:inline">Sign Out</span>
              </button>
            </nav>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="admin-util-menu" aria-label="Admin menu"
                  className="flex items-center gap-2 rounded-sm border border-gold/30 px-2.5 py-2 text-xs text-maroon transition-colors hover:bg-ivory md:hidden">
                  <span className="max-w-[120px] truncate text-ink-muted">{user.email}</span>
                  <MoreVertical size={16} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {visibleUtils.map((u) => (
                  <DropdownMenuItem key={u.to} onClick={() => navigate(u.to)} data-testid={`admin-util-mobile-${u.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className="flex items-center gap-2 text-sm">
                    <u.icon size={15} strokeWidth={1.6} /> {u.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-2 text-sm text-maroon">
                  <LogOut size={15} strokeWidth={1.6} /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <div className="p-5 lg:p-8">{children}</div>
        <nav className="flex flex-wrap gap-1 border-t border-gold/20 bg-white p-2 lg:hidden">
          {visibleGroups.flatMap((g) => g.items).map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `rounded-sm px-2 py-1 text-[11px] ${isActive ? "bg-maroon text-ivory" : "text-ink-muted"}`}>{l.label}</NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
